import { type ReactElement, useEffect, useRef, useState } from 'react';
import { ImageGallery } from '@/components/imageGallery/ImageGallery';
import { ImageCarouselButton } from '@/components/imageCarouselButton/ImageCarouselButton';
import { ImageCarouselCaption } from '@/components/imageCarouselCaption/ImageCarouselCaption';
import { Key } from 'ts-keycode-enum';
import { useSwipeable } from 'react-swipeable';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import type { HotelImage } from '@/interfaces';

interface CarouselImages {
    images?: readonly HotelImage[];
    altText: string;
    id: string;
}

const renderImages = (images: readonly HotelImage[], imageIndex: number): ReactElement => {
    const nextImageIndex = (imageIndex + 1) % images.length;
    const secondNextImageIndex = (imageIndex + 2) % images.length;
    return (
        <div data-testid="carousel-images">
            <div className="grid multi-image-container">
                <div className="grid__unit u-size2of3 image-container panorama-image-main">
                    <TransitionGroup>
                        <CSSTransition key={`image-transition-${imageIndex}`} timeout={300} classNames="fade">
                            <img
                                alt="Galleribilde"
                                src={images[imageIndex].large}
                                data-index={imageIndex}
                                data-testid={`image-lightbox-${images[imageIndex]}`}
                            />
                        </CSSTransition>
                    </TransitionGroup>
                </div>
                <div className="grid__unit u-size1of3 u-hide-lt768">
                    <div className="image-container panorama-image-extra">
                        <TransitionGroup>
                            <CSSTransition key={`image-transition-${nextImageIndex}`} timeout={300} classNames="fade">
                                <img
                                    className="u-border-top-right-radius-8px"
                                    alt="Galleribilde"
                                    src={images[nextImageIndex].large}
                                    data-index={nextImageIndex}
                                    data-testid={`image-lightbox-${images[nextImageIndex]}`}
                                />
                            </CSSTransition>
                        </TransitionGroup>
                    </div>
                    <div className="image-container panorama-image-extra">
                        <TransitionGroup>
                            <CSSTransition key={`image-transition-${secondNextImageIndex}`} timeout={300} classNames="fade">
                                <img
                                    className="u-border-bottom-right-radius-8px"
                                    alt="Galleribilde"
                                    src={images[secondNextImageIndex].large}
                                    data-index={secondNextImageIndex}
                                    data-testid={`image-lightbox-${images[secondNextImageIndex]}`}
                                />
                            </CSSTransition>
                        </TransitionGroup>
                    </div>
                </div>
            </div>
            <ImageCarouselCaption caption={images[imageIndex].caption} currentImage={imageIndex + 1} numberOfImages={images.length} />
        </div>
    );
};

export const ImageCarousel = ({ images, id, altText }: CarouselImages) => {
    const eleRef = useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [value, setValue] = useState(0);

    const handleNext = (event?: React.MouseEvent): void => {
        event?.stopPropagation();
        setValue((value + 1) % images.length);
    };

    const handlePrevious = (event?: React.MouseEvent): void => {
        event?.stopPropagation();
        setValue(value > 0 ? value - 1 : images.length - 1);
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
        if (event.metaKey || event.altKey || event.shiftKey || event.ctrlKey) {
            return;
        }

        if (event.keyCode === Key.LeftArrow) {
            event.preventDefault();
            setValue(value > 0 ? value - 1 : images.length - 1);
        }

        if (event.keyCode === Key.RightArrow) {
            event.preventDefault();
            setValue((value + 1) % images.length);
        }
    };

    const onClickOrKeyPressMain = (): void => {
        setIsOpen(!isOpen);
        return;
    };

    useEffect((): (() => void) => {
        document.body.addEventListener('keydown', handleKeyDown);

        // remove listener
        return (): void => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const swipeHandlers = useSwipeable({
        preventDefaultTouchmoveEvent: true,
        onSwipedLeft: () => handleNext(),
        onSwipedRight: () => handlePrevious(),
    });

    return (
        <div
            className="grid__unit u-size1of1 clickable"
            {...swipeHandlers}
            data-gallerylightbox-name="ad"
            data-gallerylightbox-context="mobile"
            id={id}
            data-testid="galleryButton"
            aria-label="gallery open"
            // biome-ignore lint/a11y/useSemanticElements: use <button> or <submit type=button>
            role="button"
            tabIndex={0}
            onClick={onClickOrKeyPressMain}
            onKeyPress={onClickOrKeyPressMain}>
            {images ? renderImages(images, value) : <div />}
            <ImageCarouselButton direction="left" eventHandler={handlePrevious} />
            <ImageCarouselButton direction="right" eventHandler={handleNext} />
            {images?.length ? (
                <ImageGallery
                    id={id}
                    images={images.map((image) => image.large)}
                    altText={altText}
                    imageRef={eleRef}
                    data-testid="hotelImageGallery"
                    openGallery={isOpen}
                    galleryValue={value}
                />
            ) : null}
        </div>
    );
};
